// AGC: Shared press / long-press gesture detection for the mic button.
// AGC: Used by both BottomNav and DesktopSidebar to guarantee identical voice UX across shells.
// AGC: Supports slide-down-to-cancel during hold mode (like WhatsApp).
import { useCallback, useRef, useState } from 'react'

// AGC: ms before short-tap upgrades to long-press (hold-to-talk)
const LONG_PRESS_THRESHOLD = 300
// AGC: px of downward slide to trigger cancel during hold
const SLIDE_CANCEL_DISTANCE = 80

interface Options {
  onPress?: () => void
  onLongPress?: () => void
  onLongPressEnd?: () => void
  onSlideCancel?: () => void
}

export interface MicPressGesture {
  isMicActive: boolean
  isLongPressing: boolean
  handlers: {
    onMouseDown: () => void
    onMouseUp: () => void
    onMouseLeave: () => void
    onTouchStart: (e: React.TouchEvent) => void
    onTouchMove: (e: React.TouchEvent) => void
    onTouchEnd: () => void
    onTouchCancel: () => void
  }
}

export const useMicPressGesture = ({ onPress, onLongPress, onLongPressEnd, onSlideCancel }: Options): MicPressGesture => {
  // AGC: isMicActive provides a brief visual flash on tap for button feedback
  const [isMicActive, setIsMicActive] = useState(false)
  const [isLongPressing, setIsLongPressing] = useState(false)

  const pressTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isLongPressRef = useRef(false)
  const pressStartTimeRef = useRef<number>(0)
  const touchStartYRef = useRef<number>(0)
  const slidCancelledRef = useRef(false)

  const clearPressTimer = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current)
      pressTimerRef.current = null
    }
  }, [])

  const handlePressStart = useCallback(() => {
    pressStartTimeRef.current = Date.now()
    isLongPressRef.current = false
    slidCancelledRef.current = false
    setIsLongPressing(false)
    pressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true
      setIsLongPressing(true)
      onLongPress?.()
    }, LONG_PRESS_THRESHOLD)
  }, [onLongPress])

  // AGC: Track touch start Y for slide-down detection
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (touch) touchStartYRef.current = touch.clientY
    handlePressStart()
  }, [handlePressStart])

  // AGC: Track vertical slide during hold — cancel if user slides down
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isLongPressRef.current || slidCancelledRef.current) return
    const touch = e.touches[0]
    if (!touch) return
    const deltaY = touch.clientY - touchStartYRef.current
    if (deltaY > SLIDE_CANCEL_DISTANCE) {
      slidCancelledRef.current = true
      clearPressTimer()
      isLongPressRef.current = false
      setIsLongPressing(false)
      onSlideCancel?.()
    }
  }, [clearPressTimer, onSlideCancel])

  const handlePressEnd = useCallback(() => {
    clearPressTimer()
    // AGC: If already cancelled by slide, don't fire any end callback
    if (slidCancelledRef.current) {
      slidCancelledRef.current = false
      return
    }
    const pressDuration = Date.now() - pressStartTimeRef.current
    const wasLongPress = isLongPressRef.current
    if (!wasLongPress && pressDuration < LONG_PRESS_THRESHOLD) {
      // AGC: Flash isMicActive briefly on tap so BottomNav/DesktopSidebar show visual feedback
      setIsMicActive(true)
      onPress?.()
      setTimeout(() => setIsMicActive(false), 300)
    } else if (wasLongPress) {
      // AGC: Hold-to-talk release — fire long-press end so consumer can stop+process recording
      onLongPressEnd?.()
    }
    isLongPressRef.current = false
    setIsLongPressing(false)
  }, [clearPressTimer, onPress, onLongPressEnd])

  // AGC: Cancel mid-long-press = treat as release so recording finalizes
  const handlePressCancel = useCallback(() => {
    clearPressTimer()
    if (slidCancelledRef.current) {
      slidCancelledRef.current = false
      return
    }
    if (isLongPressRef.current) onLongPressEnd?.()
    isLongPressRef.current = false
    setIsLongPressing(false)
  }, [clearPressTimer, onLongPressEnd])

  return {
    isMicActive,
    isLongPressing,
    handlers: {
      onMouseDown: handlePressStart,
      onMouseUp: handlePressEnd,
      onMouseLeave: handlePressCancel,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handlePressEnd,
      onTouchCancel: handlePressCancel,
    },
  }
}
